import { Link } from 'react-router-dom';
import { overviewTargetPath, type OverviewActivityItem } from '@scoutaround/script-breakdown-shared';

import { ACTION_LABEL } from '../../pages/tabs/ActivityTab.js';
import { personName, usePeople } from '../conversations/hooks.js';
import { Icon } from '../Icon.js';
import { When } from './parts.js';

/** The feed shows the latest few; the Activity tab keeps the whole record. */
export const RECENT_ACTIVITY_VISIBLE = 5;

function n(count: number, one: string, many: string): string {
  return count === 1 ? `a ${one}` : `${String(count)} ${many}`;
}

/**
 * One line of the feed, in words: what the PERSON did ("approved 12 location
 * requirements"), or, for the system, the whole sentence ("Draft v2 was read into
 * scenes"). `null` subject means the line is already a sentence.
 */
export function activityPhrase(item: OverviewActivityItem): { personal: boolean; text: string } {
  const c = item.count;
  const draft = item.versionNumber === null ? 'the screenplay' : `draft v${String(item.versionNumber)}`;
  const Draft = draft.charAt(0).toUpperCase() + draft.slice(1);

  switch (item.source) {
    case 'PROJECT':
      return { personal: true, text: ACTION_LABEL[item.action] ?? item.action.toLowerCase().replace(/_/g, ' ') };
    case 'SCRIPT_UPLOADED':
      return { personal: true, text: `uploaded ${draft}` };
    case 'PARSE':
      if (item.action === 'COMPLETED') return { personal: false, text: `${Draft} was read into scenes` };
      if (item.action === 'FAILED') return { personal: false, text: `${Draft} could not be read` };
      return { personal: false, text: `${Draft} is being read` };
    case 'ANALYSIS':
      if (item.action === 'COMPLETED') return { personal: false, text: `The AI analysis of ${draft} finished` };
      if (item.action === 'PARTIAL') return { personal: false, text: `The AI analysis of ${draft} finished in part` };
      if (item.action === 'FAILED') return { personal: false, text: `The AI analysis of ${draft} failed` };
      return { personal: false, text: `The AI analysis of ${draft} started` };
    case 'REQUIREMENT': {
      const req = n(c, 'location requirement', 'location requirements');
      // A proposal with no person behind it is the analysis's.
      if (item.action === 'PROPOSED') {
        return item.actorUserId === null
          ? { personal: false, text: `The AI suggested ${req}` }
          : { personal: true, text: `added ${req}` };
      }
      const verb: Record<string, string> = {
        APPROVED: `approved ${req}`,
        DISMISSED: `marked ${req} not needed`,
        RESTORED: `restored ${req}`,
        MERGED: c === 1 ? 'merged location requirements' : `merged location requirements ${String(c)} times`,
        SPLIT: c === 1 ? 'split a location requirement' : `split ${String(c)} location requirements`,
        SOURCING_ACTIVATED: `started searching for ${n(c, 'location', 'locations')}`,
        SOURCING_PAUSED: `paused the search for ${n(c, 'location', 'locations')}`,
      };
      return { personal: item.actorUserId !== null, text: verb[item.action] ?? `changed ${req}` };
    }
    case 'ELEMENT_DECISION': {
      const el = n(c, 'production element', 'production elements');
      const verb: Record<string, string> = {
        ACCEPT: `confirmed ${el}`,
        EDIT: `corrected ${el}`,
        REJECT: `rejected ${n(c, 'suggested element', 'suggested elements')}`,
        RESTORE: `restored ${el}`,
      };
      return { personal: true, text: verb[item.action] ?? `decided on ${el}` };
    }
    case 'SCENE_CONFIRMED':
      return { personal: true, text: `confirmed ${n(c, 'scene', 'scenes')}` };
    case 'SCENE_SIGNED_OFF':
      return { personal: true, text: `signed off ${n(c, 'scene breakdown', 'scene breakdowns')}` };
  }
}

/**
 * WHAT HAPPENED LATELY — quieter than the actions above it, on purpose.
 *
 * Its own request (`GET …/overview/activity`), so a feed that fails or is slow
 * never holds up the rest of the page; it says it could not load and offers to
 * try again, and never shows an empty list in place of an error.
 */
export function RecentActivity({
  projectId,
  address,
  query,
}: {
  projectId: string;
  address: string;
  query: {
    data?: { items: OverviewActivityItem[] };
    isPending: boolean;
    isError: boolean;
    refetch: () => unknown;
  };
}) {
  const people = usePeople(projectId);
  const items = query.data?.items.slice(0, RECENT_ACTIVITY_VISIBLE);

  return (
    <section className="cc-panel" data-testid="overview-activity" aria-labelledby="overview-activity-title">
      <div className="cc-panel-head">
        <h2 className="cc-panel-title" id="overview-activity-title">
          <Icon name="clock" size={18} />
          Recent activity
        </h2>
        <Link
          to={overviewTargetPath(address, { kind: 'TAB', tab: 'ACTIVITY' })}
          className="cc-link"
          data-testid="overview-activity-all"
        >
          View activity
          <Icon name="arrowRight" size={14} />
        </Link>
      </div>

      {query.isError ? (
        <div data-testid="overview-activity-error">
          <p className="cc-note" style={{ margin: 0, fontSize: 12.5 }}>
            Recent activity could not be loaded. Nothing is lost.
          </p>
          <button
            type="button"
            className="ctl"
            style={{ marginTop: 8 }}
            data-testid="overview-activity-retry"
            onClick={() => {
              query.refetch();
            }}
          >
            Try again
          </button>
        </div>
      ) : query.isPending ? (
        <p className="cc-note" role="status" data-testid="overview-activity-loading" style={{ margin: 0 }}>
          Loading recent activity…
        </p>
      ) : items === undefined || items.length === 0 ? (
        <p className="cc-note" data-testid="overview-activity-empty" style={{ margin: 0, fontSize: 12.5 }}>
          Nothing recorded yet. Uploads, reviews and decisions appear here as they happen.
        </p>
      ) : (
        <ol className="cc-feed">
          {items.map((item) => {
            const { personal, text } = activityPhrase(item);
            const who =
              personal && item.actorUserId !== null ? personName(people.data?.people, item.actorUserId, null) : null;
            const sentence = who === null ? text.charAt(0).toUpperCase() + text.slice(1) : `${who} ${text}`;

            return (
              <li key={item.id} className="cc-feed-item" data-testid={`overview-activity-item-${item.id}`}>
                {item.target === null ? (
                  <span>{sentence}</span>
                ) : (
                  <Link to={overviewTargetPath(address, item.target)}>{sentence}</Link>
                )}
                <span className="cc-feed-time">
                  <When at={item.at} />
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
